import type { Control } from "../core/Control.js";
import type { Point } from "../../common/geometry.js";
import { Page, type PageOptions } from "./Page.js";
import { Panel, type PanelOptions } from "./Panel.js";

/**
 * The four bands of a scene, back to front.
 *
 * - `background` - sky, horizon, stars: art, never touched.
 * - `stage` - a 3D viewport or anything else big and behind the controls.
 * - `content` - the controls somebody reads and presses.
 * - `foreground` - embers, sparks, a vignette: art over everything, never touched.
 */
export type SceneLayerKind = "background" | "stage" | "content" | "foreground";

export const SCENE_LAYERS: readonly SceneLayerKind[] = ["background", "stage", "content", "foreground"];

/**
 * One band of a `Scene`.
 *
 * The two art bands refuse the pointer outright, children and all, which is the rule that makes
 * a scene easy to build: embers drifting over a button can never swallow its click, and a sky
 * can never be the thing a drag lands on. The other two let a press through wherever they have
 * nothing of their own, so the stage behind the content is still there to be dragged.
 */
export class SceneLayer extends Panel {
  readonly kind: SceneLayerKind;

  constructor(kind: SceneLayerKind, options: PanelOptions = {}) {
    super({ dock: "fill", style: "none", clickThrough: true, ...options });
    this.kind = kind;
  }

  /** True for the art bands, which never take input. */
  get decorative(): boolean {
    return this.kind === "background" || this.kind === "foreground";
  }

  override hitTest(p: Point): Control | null {
    if (this.decorative) return null;
    return super.hitTest(p);
  }
}

/**
 * A screen built in layers: art behind, a stage, the controls, art in front.
 *
 * Every screen worth looking at in the samples this was drawn from - a loading screen, a
 * character select, a landing page - is the same four bands, and each one was written as two
 * hand-painted controls with the real ones sandwiched between. A scene is that arrangement as a
 * container, so the bands are places to put things rather than code to write.
 *
 * It is a `Page`, so a scene loaded on its own fills the desktop behind every window, and one
 * loaded into a pane fills the pane.
 *
 * ```ts
 * const scene = new Scene();
 * scene.backgroundLayer.controls.add(sky);
 * scene.contentLayer.controls.add(title);
 * scene.foregroundLayer.controls.add(embers);
 * scene.show(desktop);
 * ```
 */
export class Scene extends Page {
  #layers: Record<SceneLayerKind, SceneLayer>;

  constructor(options: PageOptions = {}) {
    super(options);
    this.#layers = {
      background: new SceneLayer("background"),
      stage: new SceneLayer("stage"),
      content: new SceneLayer("content"),
      foreground: new SceneLayer("foreground"),
    };
    for (const kind of SCENE_LAYERS) this.controls.add(this.#layers[kind]);
  }

  get backgroundLayer(): SceneLayer {
    return this.#layers.background;
  }
  get stageLayer(): SceneLayer {
    return this.#layers.stage;
  }
  get contentLayer(): SceneLayer {
    return this.#layers.content;
  }
  get foregroundLayer(): SceneLayer {
    return this.#layers.foreground;
  }

  layer(kind: SceneLayerKind): SceneLayer {
    return this.#layers[kind];
  }

  /**
   * Swap in a layer built elsewhere - what markup does with a `<Background>` element - keeping
   * the band's place in the stack. The one it replaces is dropped with its children.
   */
  setLayer(layer: SceneLayer): void {
    const old = this.#layers[layer.kind];
    if (old === layer) return;
    const at = this.controls.indexOf(old);
    this.controls.remove(old);
    this.#layers[layer.kind] = layer;
    this.controls.insert(Math.max(0, at), layer);
  }
}
